/**
 * E34 · DEFAULT HOMEWORK INSTRUCTIONS — founder "Default instructions: A" (2026-10-25), the same rule as the default titles
 * (`homeworkTitles.ts`, Q-ru-3 A): instructions are stored only when the therapist wrote or edited them; a tool's default left as
 * it is travels as `instructions: null` and every reader renders the default in ITS language (the phone in the client's, the
 * portal in the therapist's). Facts written before the ruling carry the text they were given and read as before.
 *
 * The text of each default is the i18n string the tool's Assign button already used (`translationEn` / `translationRu`);
 * this table only names which key belongs to which target — the ONE place a default instruction is named. Unlike a title, an
 * instruction is optional: a target without a default simply has none (null).
 */
export const HOMEWORK_DEFAULT_INSTRUCTION_KEYS = {
  // E47 · Schema modes
  modeCheckIn: 'modes.hub.assign.defaultInstructions',
  // E45 · the sleep diary
  sleepDiary: 'sleep.hub.assign.defaultInstructions',
  // E48 · Functional analysis
  chainAnalysis: 'fa.assign.defaultInstructions',
  // E49 · Motivation
  changeRating: 'mi.hub.assign.defaultInstructions.changeRating',
  decisionalBalance: 'mi.hub.assign.defaultInstructions.decisionalBalance',
  changePlan: 'mi.hub.assign.defaultInstructions.changePlan',
} as const;

export type HomeworkDefaultInstructionTarget = keyof typeof HOMEWORK_DEFAULT_INSTRUCTION_KEYS;

type InstructionTarget = { type?: string | null };

/** What a reader needs to word an assignment's instructions — every homework read model row carries it. */
export type HomeworkInstructed = {
  kind?: string | null;
  target?: InstructionTarget | null;
  /** The therapist's own instructions; null (or absent) = the target's default, if it has one. */
  instructions?: string | null;
};

/** A translator fixed to the reader's language (i18next's `TFunction` qualifies). */
export type HomeworkInstructionsTranslate = (key: string, options?: Record<string, unknown>) => string;

const hasOwn = (key: string): key is HomeworkDefaultInstructionTarget =>
  Object.prototype.hasOwnProperty.call(HOMEWORK_DEFAULT_INSTRUCTION_KEYS, key);

/** The i18n key of a target's default instructions — only a TOOL can have one, and only some do. */
export const homeworkDefaultInstructionKey = (
  kind: string | null | undefined,
  target: InstructionTarget | null | undefined,
): string | null => {
  const type = target?.type;
  return kind === 'tool' && type && hasOwn(type) ? HOMEWORK_DEFAULT_INSTRUCTION_KEYS[type] : null;
};

/**
 * The assignment's instructions for ONE reader: the therapist's own text as written, else the target's default rendered with the
 * reader's translator, else null (nothing to show).
 */
export const homeworkInstructionsOf = (row: HomeworkInstructed, t: HomeworkInstructionsTranslate): string | null => {
  if (row.instructions && row.instructions.trim()) return row.instructions;
  const key = homeworkDefaultInstructionKey(row.kind, row.target);
  return key ? t(key) : null;
};

/**
 * Is a typed text the target's default instructions in one of the given languages? The API's net for a client that still sends
 * the default's TEXT (a portal bundle from before the ruling, another caller): a text equal to a shipped default is not an edit.
 */
export const isHomeworkDefaultInstructions = (
  kind: string | null | undefined,
  target: InstructionTarget | null | undefined,
  text: string,
  translators: readonly HomeworkInstructionsTranslate[],
): boolean => {
  const key = homeworkDefaultInstructionKey(kind, target);
  const typed = text.trim();
  return !!key && !!typed && translators.some((t) => t(key).trim() === typed);
};
